import cx from "classnames";

/**
 * Tailwind group name every draggable-handle parent must apply so the shared
 * `ResizeHandleLine` can react to hover / focus. Kept as a NAMED group (not the
 * bare `group`) so a handle nested inside another `group` ancestor (e.g. the
 * sidebar root, which is `group peer`) never lights up from unrelated hovers.
 */
export const RESIZE_HANDLE_GROUP_CLASS = "group/resize";

export type ResizeHandleLineProps = {
  /** Force the thick "accent" state while a drag is in progress. */
  active?: boolean;
  /** Vertical divider (default) or horizontal divider. */
  orientation?: "vertical" | "horizontal";
  /**
   * Paint the 1px hairline at rest (default). Set false when an adjacent
   * container already draws the rest hairline (e.g. the sidebar's `border-r`)
   * so the overlay stays invisible until hover/drag and never doubles the line.
   */
  showAtRest?: boolean;
  /** Extra classes for consumer-specific hover wiring. Keep minimal. */
  className?: string;
  testId?: string;
};

/**
 * The single, standard draggable-resize line used by EVERY resize handle in the
 * app — Ask Puntt panes, the sidebar rail, and any future draggable seam.
 *
 * Contract (why this exists as one component):
 * - 1px hairline at rest, 2px on hover / focus-visible / active drag.
 * - It grows from an absolutely-positioned, CENTERED element, so widening it on
 *   hover never reflows adjacent panes — zero layout shift, ever.
 * - Rest = neutral gray, hover = darker gray, focus = accent, drag = accent.
 *
 * The parent handle MUST include `RESIZE_HANDLE_GROUP_CLASS` and establish a
 * positioning context (`relative`).
 */
export function ResizeHandleLine({
  active = false,
  orientation = "vertical",
  showAtRest = true,
  className,
  testId,
}: ResizeHandleLineProps) {
  const vertical = orientation === "vertical";

  const base = cx(
    "pointer-events-none absolute z-10 duration-150 ease-out",
    vertical
      ? "inset-y-0 left-1/2 -translate-x-1/2 transition-[width,background-color]"
      : "inset-x-0 top-1/2 -translate-y-1/2 transition-[height,background-color]",
  );

  const state = active
    ? cx(vertical ? "w-0.5" : "h-0.5", "bg-puntt-accent-10")
    : cx(
        vertical ? "w-px" : "h-px",
        vertical
          ? // CSS group-hover works for wide hit targets (e.g. SidebarRail w-4 button).
            // For zero-width PanelResizeHandle tracks the library sets
            // data-resize-handle-state="hover" based on hitAreaMargins — CSS :hover
            // never fires on a w-0 element, so we also react to that attribute.
            "group-hover/resize:w-0.5 group-focus-visible/resize:w-0.5 group-data-[resize-handle-state=hover]/resize:w-0.5"
          : "group-hover/resize:h-0.5 group-focus-visible/resize:h-0.5 group-data-[resize-handle-state=hover]/resize:h-0.5",
        showAtRest ? "bg-puntt-neutral-gray-6" : "bg-transparent",
        "group-hover/resize:bg-puntt-neutral-gray-8 group-focus-visible/resize:bg-puntt-accent-9 group-data-[resize-handle-state=hover]/resize:bg-puntt-neutral-gray-8",
      );

  return (
    <span
      aria-hidden="true"
      data-testid={testId}
      className={cx(base, state, className)}
    />
  );
}
